import React from 'react';
import {Container, Header} from '@amzn/awsui-components-react';
import ReactEcharts from 'echarts-for-react';
import {SeriesOption} from 'echarts/types/dist/shared';
import {chartColorsLight} from '../../common/colors';
import type {ContributionGroup, ForecastBand} from '../model-explainer';

const COMPONENT_TEXTS = {
    chart_description: 'Each metric shows the contribution to the final forecast.',
} as const;

/**
 * The set of series varies with the data: bands and actuals appear only when
 * the window holds their columns. ECharts' default merge keeps any series the
 * previous option declared but the new one omits, so narrowing the window
 * from the forecast into the actuals period left the old bands (and their
 * dates on the axis) behind. Replacing the series list on every update, with
 * stable ids so unchanged series are merged rather than recreated, keeps the
 * chart equal to its props while preserving the user's legend selections.
 */
const SERIES_REPLACE_MERGE = ['series'];

/**
 * Prediction-interval bands are drawn as a pair of stacked line series: an
 * invisible one carrying the lower bound, and a second carrying the interval
 * height with an area fill. The lower-bound series exists only to lift the fill
 * off the axis, so it is kept out of the legend and out of the tooltip by name.
 */
const BAND_BASE_SERIES_PREFIX = '__band_base__';

const BAND_FILL_COLOR = chartColorsLight[0];
/** Widest band first, so narrower bands are drawn on top with a stronger fill. */
const BAND_OPACITIES = [0.13, 0.22];
const ACTUALS_COLOR = chartColorsLight[6];

function toNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** A band is renderable only when both of its bounds are present in the data. */
function resolveAvailableBands(data: any[], forecastBands: ForecastBand[]): ForecastBand[] {
    return forecastBands.filter((band) =>
        data.some((row) => toNumber(row[band.lowerColumn]) !== null && toNumber(row[band.upperColumn]) !== null)
    );
}

function hasColumnData(data: any[], column?: string): boolean {
    return !!column && data.some((row) => toNumber(row[column]) !== null);
}

/**
 * First date carrying a forecast value. Used to mark where the actuals end and
 * the forecast begins.
 */
function resolveForecastStartDate(data: any[], forecastColumns: string[]): string | undefined {
    return data.find((row) => forecastColumns.some((column) => toNumber(row[column]) !== null))?.balanceDate;
}

/**
 * Pixel layout used when the contributions are split into groups. The panel
 * count varies per model, so a fixed pixel height per panel keeps every panel
 * readable instead of squeezing them into a fixed chart height.
 */
const FORECAST_GRID_TOP = 40;
const FORECAST_GRID_HEIGHT = 260;
/** Room below each grid for the x-axis labels and name. */
const AXIS_SPACE = 60;
/** Room above each group grid for its title and legend. */
const GROUP_HEADER_HEIGHT = 56;
const GROUP_LEGEND_OFFSET = 24;
const GROUP_GRID_HEIGHT = 160;

function buildAxes(gridCount: number) {
    const indexes = Array.from({length: gridCount}, (_, index) => index);
    return {
        xAxis: indexes.map((gridIndex) => ({
            gridIndex,
            type: 'category',
            name: 'Date',
            nameLocation: 'middle',
            nameGap: 30,
            boundaryGap: false,
        })),
        yAxis: indexes.map((gridIndex) => ({
            gridIndex,
            type: 'value',
            name: 'Value',
            nameLocation: 'middle',
            nameGap: 50,
        })),
    };
}

/**
 * Without groups the original two-panel layout is returned unchanged. With
 * groups, the forecast panel is followed by one titled panel per group.
 */
function buildPanelLayout(
    forecastLegendData: string[],
    groups: ContributionGroup[] | undefined,
    contributionLegendData: string[][]
) {
    if (!groups) {
        return {
            height: '50vh',
            title: undefined,
            legend: [
                {
                    data: forecastLegendData,
                    top: '0%',
                    left: 'center',
                    type: 'scroll',
                },
                {
                    data: contributionLegendData[0],
                    top: '53%',
                    left: 'center',
                    type: 'scroll',
                },
            ],
            grid: [
                {
                    top: '7%',
                    height: '35%',
                },
                {
                    top: '60%',
                    height: '35%',
                },
            ],
            ...buildAxes(2),
        };
    }

    const groupTops = groups.map(
        (_, index) =>
            FORECAST_GRID_TOP + FORECAST_GRID_HEIGHT + AXIS_SPACE + index * (GROUP_HEADER_HEIGHT + GROUP_GRID_HEIGHT + AXIS_SPACE)
    );
    const height =
        FORECAST_GRID_TOP +
        FORECAST_GRID_HEIGHT +
        AXIS_SPACE +
        groups.length * (GROUP_HEADER_HEIGHT + GROUP_GRID_HEIGHT + AXIS_SPACE);

    return {
        height: `${height}px`,
        title: groups.map((group, index) => ({
            text: group.title,
            top: groupTops[index],
            left: 'center',
            textStyle: {fontSize: 14},
        })),
        legend: [
            {
                data: forecastLegendData,
                top: 0,
                left: 'center',
                type: 'scroll',
            },
            ...groups.map((_, index) => ({
                data: contributionLegendData[index],
                top: groupTops[index] + GROUP_LEGEND_OFFSET,
                left: 'center',
                type: 'scroll',
            })),
        ],
        grid: [
            {
                top: FORECAST_GRID_TOP,
                height: FORECAST_GRID_HEIGHT,
            },
            ...groupTops.map((top) => ({
                top: top + GROUP_HEADER_HEIGHT,
                height: GROUP_GRID_HEIGHT,
            })),
        ],
        ...buildAxes(groups.length + 1),
    };
}

export default function ModelExplainerLineChart({
    data,
    forecastColumns,
    contributionColumns,
    contributionGroups,
    friendlyNameMapping,
    forecastBands = [],
    actualsColumn,
}: {
    data: any[];
    forecastColumns: string[];
    contributionColumns: string[];
    contributionGroups?: ContributionGroup[];
    friendlyNameMapping: Record<string, string>;
    forecastBands?: ForecastBand[];
    actualsColumn?: string;
}) {
    const groups = contributionGroups && contributionGroups.length > 0 ? contributionGroups : undefined;
    // Grid 0 is the forecast panel; panel i of the contributions is grid i + 1.
    const contributionPanels = groups ? groups.map((group) => group.columns) : [contributionColumns];

    // Both additions are driven by what the data actually contains rather than
    // by configuration alone, so a model run produced before these insight
    // types existed renders exactly as it did before.
    const availableBands = resolveAvailableBands(data, forecastBands);
    const showActuals = hasColumnData(data, actualsColumn);
    const forecastStartDate = showActuals ? resolveForecastStartDate(data, forecastColumns) : undefined;

    const series: SeriesOption[] = forecastColumns.map((col) => ({
        id: `forecast:${col}`,
        name: friendlyNameMapping[col],
        type: 'line',
        encode: {
            x: 'balanceDate',
            y: col,
        },
        symbol: 'none',
        lineStyle: {
            width: 2,
        },
        xAxisIndex: 0,
        yAxisIndex: 0,
        emphasis: {
            disabled: true,
        },
    }));

    const series2: SeriesOption[] = contributionPanels.flatMap((columns, panelIndex) =>
        columns.map((col) => ({
            id: `contribution:${col}`,
            name: friendlyNameMapping[col],
            type: 'line',
            encode: {
                x: 'balanceDate',
                y: col,
            },
            symbol: 'none',
            lineStyle: {
                width: 2,
            },
            xAxisIndex: panelIndex + 1,
            yAxisIndex: panelIndex + 1,
            emphasis: {
                disabled: true,
            },
        }))
    ) as SeriesOption[];

    // Appended after the existing series so palette assignment for those is
    // unchanged; `z` keeps the fills behind the forecast lines regardless.
    const bandSeries: SeriesOption[] = availableBands.flatMap((band, index) => {
        const bounds = data.map((row) => ({
            balanceDate: row.balanceDate,
            lower: toNumber(row[band.lowerColumn]),
            upper: toNumber(row[band.upperColumn]),
        }));
        const opacity = BAND_OPACITIES[Math.min(index, BAND_OPACITIES.length - 1)];
        const stack = `forecast-band-${index}`;

        return [
            {
                id: `band-base:${band.label}`,
                name: `${BAND_BASE_SERIES_PREFIX}${band.label}`,
                type: 'line',
                stack,
                // The explainer does not clip the posterior, so a low-volume
                // node can have a negative lower bound. ECharts' default
                // 'samesign' strategy would then put the base and the fill in
                // different stacks and anchor the band at zero instead of the
                // true bound.
                stackStrategy: 'all',
                data: bounds.map(({balanceDate, lower, upper}) => [balanceDate, lower !== null && upper !== null ? lower : null]),
                symbol: 'none',
                lineStyle: {opacity: 0},
                silent: true,
                legendHoverLink: false,
                z: 1,
                xAxisIndex: 0,
                yAxisIndex: 0,
                emphasis: {disabled: true},
            },
            {
                id: `band:${band.label}`,
                name: band.label,
                type: 'line',
                stack,
                stackStrategy: 'all',
                data: bounds.map(({balanceDate, lower, upper}) => [
                    balanceDate,
                    lower !== null && upper !== null ? upper - lower : null,
                ]),
                symbol: 'none',
                lineStyle: {opacity: 0},
                areaStyle: {color: BAND_FILL_COLOR, opacity},
                silent: true,
                legendHoverLink: false,
                z: 1,
                xAxisIndex: 0,
                yAxisIndex: 0,
                emphasis: {disabled: true},
            },
        ] as SeriesOption[];
    });

    const actualsSeries: SeriesOption[] =
        showActuals && actualsColumn
            ? [
                  {
                      id: `actuals:${actualsColumn}`,
                      name: friendlyNameMapping[actualsColumn] ?? actualsColumn,
                      type: 'scatter',
                      data: data
                          .map((row) => ({balanceDate: row.balanceDate, value: toNumber(row[actualsColumn])}))
                          .filter(({value}) => value !== null)
                          .map(({balanceDate, value}) => [balanceDate, value]),
                      symbolSize: 6,
                      itemStyle: {color: ACTUALS_COLOR},
                      z: 3,
                      xAxisIndex: 0,
                      yAxisIndex: 0,
                      emphasis: {disabled: true},
                      // Always declared, with no data when the window ends
                      // before the forecast starts: a merge into the same
                      // series keeps an omitted markLine, so leaving it out
                      // would carry the previous window's marker over.
                      markLine: {
                          silent: true,
                          symbol: 'none',
                          data: forecastStartDate ? [{xAxis: forecastStartDate}] : [],
                          lineStyle: {type: 'dashed', color: ACTUALS_COLOR},
                          label: {formatter: 'Forecast start', position: 'insideEndTop'},
                      },
                  },
              ]
            : [];

    const forecastLegendData = [
        ...forecastColumns.map((col) => friendlyNameMapping[col]),
        ...availableBands.map((band) => band.label),
        ...(showActuals && actualsColumn ? [friendlyNameMapping[actualsColumn] ?? actualsColumn] : []),
    ];

    const actualsSeriesName = actualsColumn ? friendlyNameMapping[actualsColumn] ?? actualsColumn : undefined;
    const seriesNameToColumn = new Map<string, string>([
        ...forecastColumns.map((col) => [friendlyNameMapping[col], col] as [string, string]),
        ...contributionColumns.map((col) => [friendlyNameMapping[col], col] as [string, string]),
        ...(showActuals && actualsColumn && actualsSeriesName ? [[actualsSeriesName, actualsColumn] as [string, string]] : []),
    ]);
    const forecastPanelSeriesNames = new Set<string>([
        ...forecastColumns.map((col) => friendlyNameMapping[col]),
        ...(showActuals && actualsSeriesName ? [actualsSeriesName] : []),
    ]);

    const {height: chartHeight, ...layout} = buildPanelLayout(
        forecastLegendData,
        groups,
        contributionPanels.map((columns) => columns.map((col) => friendlyNameMapping[col]))
    );

    const option = {
        dataset: {
            dimensions: [
                'balanceDate',
                ...contributionColumns,
                ...forecastColumns,
                ...availableBands.flatMap((band) => [band.lowerColumn, band.upperColumn]),
                ...(showActuals && actualsColumn ? [actualsColumn] : []),
            ],
            source: data,
        },
        tooltip: buildTooltipOption(
            data,
            seriesNameToColumn,
            forecastPanelSeriesNames,
            availableBands,
            showActuals ? actualsColumn : undefined
        ),
        axisPointer: {
            type: 'cross',
            link: [
                {
                    xAxisIndex: 'all',
                },
            ],
        },
        color: chartColorsLight,
        ...(layout.title ? {title: layout.title} : {}),
        legend: layout.legend,
        grid: layout.grid,
        xAxis: layout.xAxis,
        yAxis: layout.yAxis,
        series: [...series, ...series2, ...bandSeries, ...actualsSeries] as SeriesOption[],
    };

    return (
        <Container
            header={
                <Header variant="h2" description={COMPONENT_TEXTS.chart_description}>
                    Forward Looking Explainability
                </Header>
            }
        >
            <ReactEcharts
                option={option}
                replaceMerge={SERIES_REPLACE_MERGE}
                style={{height: chartHeight}}
                opts={{renderer: 'svg'}}
                data-testid="page-line-chart"
            />
        </Container>
    );
}

/**
 * Without bands or actuals the tooltip is left exactly as it was. With them, a
 * formatter is needed: the band series carry stacked offsets rather than the
 * quantile values themselves, so their bounds are read back from the row and
 * rendered as a range, and the invisible lower-bound helper series is dropped.
 */
function buildTooltipOption(
    data: any[],
    seriesNameToColumn: Map<string, string>,
    forecastPanelSeriesNames: Set<string>,
    availableBands: ForecastBand[],
    actualsColumn?: string
) {
    if (availableBands.length === 0 && !actualsColumn) {
        return {
            trigger: 'axis',
            valueFormatter: (value: number) => value?.toFixed(0),
        };
    }

    const bandLabels = new Set(availableBands.map((band) => band.label));
    const rowsByDate = new Map<string, any>(data.map((row) => [row.balanceDate, row]));
    const format = (value: number | null) => (value === null ? '-' : value.toFixed(0));

    return {
        trigger: 'axis',
        formatter: (params: any) => {
            const entries = Array.isArray(params) ? params : [params];
            if (entries.length === 0) {
                return '';
            }

            const axisValue = entries[0].axisValue;
            const row = rowsByDate.get(axisValue);
            const lines = [`<b>${axisValue}</b>`];

            // Values are read from the row by column rather than from the
            // ECharts payload, which differs in shape between dataset-encoded
            // series and the band/actuals series that carry their own data.
            entries
                .filter((entry: any) => !entry.seriesName?.startsWith(BAND_BASE_SERIES_PREFIX) && !bandLabels.has(entry.seriesName))
                .forEach((entry: any) => {
                    const column = seriesNameToColumn.get(entry.seriesName);
                    const value = column && row ? toNumber(row[column]) : null;
                    lines.push(`${entry.marker} ${entry.seriesName}: ${format(value)}`);
                });

            // Bands belong to the forecast panel only; suppress them when the
            // pointer is over the contributions panel.
            const hoveringForecastPanel = entries.some((entry: any) => forecastPanelSeriesNames.has(entry.seriesName));
            if (row && hoveringForecastPanel) {
                availableBands.forEach((band) => {
                    const lower = toNumber(row[band.lowerColumn]);
                    const upper = toNumber(row[band.upperColumn]);
                    if (lower !== null && upper !== null) {
                        lines.push(`${band.label}: ${format(lower)} – ${format(upper)}`);
                    }
                });
            }

            return lines.join('<br/>');
        },
    };
}
